/**
 * Native Health Integration Hook
 * 
 * Unified interface for Apple HealthKit (iOS) and Google Health Connect (Android)
 * Uses @capgo/capacitor-health plugin for cross-platform health data access.
 * 
 * This provides FREE access to health data from:
 * - Apple Watch (via HealthKit)
 * - WHOOP, Garmin, Oura, Fitbit (via their sync to HealthKit/Health Connect)
 * - Samsung Galaxy Watch, Wear OS devices (via Health Connect)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import HealthKitWater from '@/plugins/HealthKitWaterPlugin';

// Types for health data
export interface HealthKitReading {
  value: number;
  startDate: Date;
  endDate: Date;
  unit: string;
  sourceName?: string;
}

export interface HealthKitSource {
  name: string;
  lastSeen: Date;
}

export interface HealthKitSleepSample {
  startDate: Date;
  endDate: Date;
  value: 'awake' | 'asleep' | 'inBed' | 'rem' | 'core' | 'deep';
  sourceName?: string;
}

export interface HealthKitWorkout {
  id: string;
  activityType: string;
  startDate: Date;
  endDate: Date;
  duration: number;
  energyBurned?: number;
  distance?: number;
  sourceName?: string;
}

export interface BloodPressureReading {
  systolic: number;
  diastolic: number;
  startDate: Date;
  sourceName?: string;
}

export interface NutritionSummary {
  caloriesConsumed: number;
  caffeine: number;
  sodium: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface NativeHealthData {
  heartRate: HealthKitReading | null;
  restingHeartRate: HealthKitReading | null;
  hrv: HealthKitReading | null;
  steps: number;
  calories: number;
  distance: number;
  sleep: HealthKitSleepSample[];
  workouts: HealthKitWorkout[];
  oxygenSaturation: HealthKitReading | null;
  respiratoryRate: HealthKitReading | null;
  weight: HealthKitReading | null;
  bodyTemperature: HealthKitReading | null;
  lastSyncAt: Date | null;
  sources: HealthKitSource[];
  /** Whether a workout is currently active (HR updates every 1-3s) */
  isWorkoutActive: boolean;
  /** Data delivery mode: 'polling' | 'observer' | 'ble-stream' */
  deliveryMode: 'polling' | 'observer' | 'ble-stream';
  // --- Expanded health data types ---
  bodyFat: HealthKitReading | null;
  height: HealthKitReading | null;
  bloodPressure: BloodPressureReading | null;
  bloodGlucose: HealthKitReading | null;
  basalBodyTemperature: HealthKitReading | null;
  basalCalories: number;
  flightsClimbed: number;
  distanceCycling: number;
  exerciseTime: number;
  totalCalories: number;
  nutrition: NutritionSummary | null;
}

export interface UseNativeHealthKitReturn {
  isAvailable: boolean;
  isAuthorized: boolean;
  isLoading: boolean;
  error: string | null;
  healthData: NativeHealthData;
  requestAuthorization: () => Promise<boolean>;
  syncHealthData: (forceSync?: boolean) => Promise<void>;
  getLatestHeartRate: () => Promise<number | null>;
  getTodaySteps: () => Promise<number>;
  getSleepData: (days?: number) => Promise<HealthKitSleepSample[]>;
  getWorkouts: (days?: number) => Promise<HealthKitWorkout[]>;
  startBackgroundSync: () => Promise<void>;
  stopBackgroundSync: () => void;
}

// Permission types we'll request
const HEALTH_PERMISSIONS = {
  read: [
    'steps',
    'heartRate',
    'heartRateVariability',
    'oxygenSaturation',
    'calories',
    'distance',
    'sleep',
    'workouts',
    'restingHeartRate',
    'respiratoryRate',
    'weight',
    'bodyTemperature',
    // Expanded data types — smart scales, BP monitors, glucose, etc.
    'bodyFat',
    'height',
    'bloodPressure',
    'bloodGlucose',
    'basalCalories',
    'flightsClimbed',
    'distanceCycling',
    'exerciseTime',
    'basalBodyTemperature',
    'totalCalories',
    // NOTE: 'water' / 'dietaryWater' is NOT supported by @capgo/capacitor-health.
    // Water intake permissions are requested separately via our custom
    // HealthKitWaterPlugin (see requestAuthorization below).
    // NOTE: Nutrition types (dietary energy, caffeine, sodium, protein, carbs, fat)
    // are read via the custom HealthKitWaterPlugin.readNutritionSummary().
  ],
  write: [] as string[], // We only read, not write
};

const extractUnsupportedHealthDataType = (message: string): string | null => {
  const match = message.match(/Unsupported health data type:\s*([A-Za-z0-9_\.]+)/i);
  if (!match?.[1]) return null;
  return match[1].replace(/[^A-Za-z0-9_]/g, '');
};

const normalizeHealthSourceName = (name?: string, sourceId?: string): string | undefined => {
  // Normalize all Unicode whitespace (including non-breaking spaces \u00A0) to regular spaces
  const sourceName = (name || '').replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ').trim();
  const sourceIdLower = (sourceId || '').toLowerCase();

  if (/^bluetooth device$/i.test(sourceName)) {
    if (sourceIdLower.includes('whoop')) return 'WHOOP';
    if (sourceIdLower.includes('oura')) return 'Oura';
    if (sourceIdLower.includes('fitbit')) return 'Fitbit';
    if (sourceIdLower.includes('garmin')) return 'Garmin';
    if (sourceIdLower.includes('apple')) return 'Apple Watch';
    if (sourceIdLower.includes('polar')) return 'Polar';
    if (sourceIdLower.includes('suunto')) return 'Suunto';
    if (sourceIdLower.includes('amazfit') || sourceIdLower.includes('zepp')) return 'Amazfit';
    if (sourceIdLower.includes('coros')) return 'COROS';
    if (sourceIdLower.includes('samsung') || sourceIdLower.includes('galaxy')) return 'Samsung';
    if (sourceIdLower.includes('withings')) return 'Withings';
    if (sourceIdLower.includes('huawei')) return 'Huawei';
    if (sourceIdLower.includes('xiaomi') || sourceIdLower.includes('mi.')) return 'Xiaomi';
    if (sourceIdLower.includes('biostrap')) return 'Biostrap';
    if (sourceIdLower.includes('peloton')) return 'Peloton';
    if (sourceIdLower.includes('hidratespark') || sourceIdLower.includes('hidrate')) return 'HidrateSpark';
    if (sourceIdLower.includes('larq')) return 'LARQ';
    return 'Bluetooth Wearable';
  }

  const lower = sourceName.toLowerCase();
  if (lower.includes('whoop')) return 'WHOOP';
  if (lower.includes('apple watch')) return 'Apple Watch';
  if (lower.includes('fitbit')) return 'Fitbit';
  if (lower.includes('garmin')) return 'Garmin';
  if (lower.includes('oura')) return 'Oura';
  if (lower.includes('polar')) return 'Polar';
  if (lower.includes('suunto')) return 'Suunto';
  if (lower.includes('amazfit') || lower.includes('zepp')) return 'Amazfit';
  if (lower.includes('coros')) return 'COROS';
  if (lower.includes('samsung') || lower.includes('galaxy watch')) return 'Samsung';
  if (lower.includes('withings')) return 'Withings';
  if (lower.includes('huawei')) return 'Huawei';
  if (lower.includes('xiaomi') || lower.includes('mi band')) return 'Xiaomi';
  if (lower.includes('biostrap')) return 'Biostrap';
  if (lower.includes('peloton')) return 'Peloton';
  if (lower.includes('hidratespark') || lower.includes('hidrate')) return 'HidrateSpark';
  if (lower.includes('larq')) return 'LARQ';
  if (lower.includes('dexcom')) return 'Dexcom';
  if (lower.includes('freestyle') || lower.includes('abbott')) return 'FreeStyle Libre';
  if (lower.includes('omron')) return 'Omron';
  if (lower.includes('eufy')) return 'eufy';
  if (lower.includes('renpho')) return 'Renpho';

  // Fallback: check sourceId for known brands
  if (!sourceName && sourceIdLower.includes('whoop')) return 'WHOOP';
  if (!sourceName && sourceIdLower.includes('apple')) return 'Apple Watch';
  if (!sourceName && sourceIdLower.includes('garmin')) return 'Garmin';
  if (!sourceName && sourceIdLower.includes('withings')) return 'Withings';

  // Skip generic Apple/Health sources — these aren't real devices
  if (/^(health|com\.apple\.health|apple\s*health|iphone)$/i.test(sourceName)) return undefined;
  // Also skip "Health" with non-breaking spaces
  if (/^(health|apple\s*health)$/i.test(sourceName.replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ').trim())) return undefined;

  return sourceName || undefined;
};

const HEALTH_AUTH_KEY = 'wellebit_health_authorized';

export function useNativeHealthKit(): UseNativeHealthKitReturn {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isAuthorized, setIsAuthorizedState] = useState(() => {
    // Restore persisted auth state so we don't show "Connect" on every launch
    try { return localStorage.getItem(HEALTH_AUTH_KEY) === 'true'; } catch { return false; }
  });
  const isAuthorizedRef = useRef(isAuthorized);
  const setIsAuthorized = (val: boolean) => {
    isAuthorizedRef.current = val;
    setIsAuthorizedState(val);
    try { localStorage.setItem(HEALTH_AUTH_KEY, String(val)); } catch {}
  };
  const authInProgressRef = useRef(false);
  const authStartedAt = useRef<number | null>(null);
  const syncInProgressRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [healthData, setHealthData] = useState<NativeHealthData>({
    heartRate: null,
    restingHeartRate: null,
    hrv: null,
    steps: 0,
    calories: 0,
    distance: 0,
    sleep: [],
    workouts: [],
    oxygenSaturation: null,
    respiratoryRate: null,
    weight: null,
    bodyTemperature: null,
    lastSyncAt: null,
    sources: [],
    isWorkoutActive: false,
    deliveryMode: 'polling',
    // Expanded
    bodyFat: null,
    height: null,
    bloodPressure: null,
    bloodGlucose: null,
    basalBodyTemperature: null,
    basalCalories: 0,
    flightsClimbed: 0,
    distanceCycling: 0,
    exerciseTime: 0,
    totalCalories: 0,
    nutrition: null,
  });

  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hrFastPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activityFastPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const workoutPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const appStateListenerRef = useRef<any>(null);
  const observerListenerRef = useRef<any>(null);
  const healthPluginRef = useRef<any>(null);
  const observerPluginRef = useRef<any>(null);

  // Check if running on native platform
  const isNative = Capacitor.isNativePlatform();

  // Initialize and check availability
  useEffect(() => {
    async function init() {
      if (!isNative) {
        console.log('[NativeHealthKit] Not running on native platform, health data unavailable');
        setIsAvailable(false);
        setIsLoading(false);
        return;
      }

      try {
        // Dynamically import the health plugin only on native
        const { Health } = await import('@capgo/capacitor-health');
        healthPluginRef.current = Health;

        const { available, reason } = await Health.isAvailable();
        
        if (!available) {
          console.log('[NativeHealthKit] Health APIs unavailable:', reason);
          setError(reason || 'Health data not available on this device');
          setIsAvailable(false);
        } else {
          console.log('[NativeHealthKit] Health APIs available');
          setIsAvailable(true);
        }
      } catch (err) {
        console.error('[NativeHealthKit] Error initializing:', err);
        setError('Failed to initialize health integration');
        setIsAvailable(false);
      } finally {
        setIsLoading(false);
      }
    }

    init();

    return () => {
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
      if (hrFastPollRef.current) clearInterval(hrFastPollRef.current);
      if (activityFastPollRef.current) clearInterval(activityFastPollRef.current);
      if (workoutPollRef.current) clearInterval(workoutPollRef.current);
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove?.();
      }
      if (observerListenerRef.current) {
        observerListenerRef.current.remove?.();
      }
    };
  }, [isNative]);

  // NOTE: Auto-authorization removed. HealthKit permission is now requested
  // only when the user explicitly taps "Connect Health Data" in onboarding
  // or settings. This prevents the iOS permission dialog from appearing
  // immediately on app launch before the user reaches the appropriate screen.

  // Request authorization for health data access
  const requestAuthorization = useCallback(async (): Promise<boolean> => {
    // Prevent concurrent authorization requests (double-tap crash guard)
    // Auto-reset stuck guard after 10 seconds
    if (authInProgressRef.current) {
      if (authStartedAt.current && Date.now() - authStartedAt.current > 10_000) {
        console.warn('[NativeHealthKit] Auth guard was stuck for >10s — resetting');
        authInProgressRef.current = false;
      } else {
        console.log('[NativeHealthKit] Authorization already in progress, skipping');
        return isAuthorizedRef.current;
      }
    }

    authInProgressRef.current = true;
    authStartedAt.current = Date.now();
    setIsLoading(true);
    setError(null);

    try {
      if (!isAvailable || !healthPluginRef.current) {
        console.warn('[NativeHealthKit] requestAuthorization called but not available. Retrying init...');
        // Retry initialization in case the plugin loaded late
        const { Health } = await import('@capgo/capacitor-health');
        healthPluginRef.current = Health;
        const { available, reason } = await Health.isAvailable();
        if (!available) {
          setError(reason || 'Health data not available on this device. Make sure HealthKit is enabled in Xcode capabilities.');
          return false;
        }
        setIsAvailable(true);
      }

      const Health = healthPluginRef.current;
      const readPermissions = [...HEALTH_PERMISSIONS.read];
      let authorizationResult: unknown = null;

      while (readPermissions.length > 0) {
        try {
          console.log('[NativeHealthKit] Calling requestAuthorization with read scopes:', readPermissions.join(', '));
          authorizationResult = await Health.requestAuthorization({
            read: readPermissions,
            write: HEALTH_PERMISSIONS.write,
          });
          break;
        } catch (authErr) {
          const authMsg = authErr instanceof Error ? authErr.message : String(authErr);
          const unsupportedType = extractUnsupportedHealthDataType(authMsg);
          const matchedType = unsupportedType
            ? readPermissions.find((type) => type.toLowerCase() === unsupportedType.toLowerCase())
            : undefined;

          if (matchedType) {
            console.warn(`[NativeHealthKit] ${matchedType} unsupported on this device/plugin; retrying without it`);
            const idx = readPermissions.indexOf(matchedType);
            if (idx >= 0) readPermissions.splice(idx, 1);
            continue;
          }

          throw authErr;
        }
      }

      if (!authorizationResult) {
        setError('No supported health metrics available to authorize on this device.');
        return false;
      }

      console.log('[NativeHealthKit] requestAuthorization result:', JSON.stringify(authorizationResult));

      // On iOS, requestAuthorization may report success even when the user denies.
      // We verify by attempting a small data read.
      setIsAuthorized(true);

      // Also request dietaryWater permission via our custom plugin (for LARQ / 3rd-party hydration)
      try {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        await HealthKitWater.requestAuthorization();
        await HealthKitWater.readWaterIntake({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
        });
        console.log('[NativeHealthKit] dietaryWater authorization verified');
      } catch (waterErr) {
        const waterMsg = waterErr instanceof Error ? waterErr.message : String(waterErr);
        console.warn('[NativeHealthKit] dietaryWater auth/read check failed:', waterMsg);
        if (/unimplemented|not implemented|not available|plugin is not implemented/i.test(waterMsg)) {
          console.warn('[NativeHealthKit] Hydration bridge missing — rebuild with HealthKitWaterPlugin in App target');
        }
      }

      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[NativeHealthKit] Authorization error:', msg, err);

      // Provide actionable error messages
      if (msg.includes('entitlement') || msg.includes('not available') || msg.includes('HealthKit')) {
        setError('HealthKit is not configured. In Xcode: Target → Signing & Capabilities → + Capability → HealthKit.');
      } else {
        setError(`Health authorization failed: ${msg}`);
      }
      setIsAuthorized(false);
      return false;
    } finally {
      setIsLoading(false);
      authInProgressRef.current = false;
      authStartedAt.current = null;
    }
  }, [isAvailable]);

  // Get latest heart rate reading — use readSamples (HR is discrete, not cumulative)
  const getLatestHeartRate = useCallback(async (): Promise<number | null> => {
    if (!isAuthorized || !healthPluginRef.current) return null;

    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

      const result = await Health.readSamples({
        startDate: oneHourAgo.toISOString(),
        endDate: now.toISOString(),
        dataType: 'heartRate',
      });

      const samples = (result as any).samples || [];
      if (samples.length > 0) {
        const latest = samples.reduce((acc: any, sample: any) => {
          const accTime = new Date(acc.endDate || acc.startDate).getTime();
          const sampleTime = new Date(sample.endDate || sample.startDate).getTime();
          return sampleTime > accTime ? sample : acc;
        });
        return Math.round(latest.value);
      }
      return null;
    } catch (err) {
      console.error('[NativeHealthKit] Error fetching heart rate:', err);
      return null;
    }
  }, [isAuthorized]);

  // Get today's step count
  const getTodaySteps = useCallback(async (): Promise<number> => {
    if (!isAuthorized || !healthPluginRef.current) return 0;

    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const result = await Health.queryAggregated({
        startDate: startOfDay.toISOString(),
        endDate: now.toISOString(),
        dataType: 'steps',
        bucket: 'day',
      });

      if (result.samples && result.samples.length > 0) {
        return Math.round(result.samples[0].value);
      }
      return 0;
    } catch (err) {
      console.error('[NativeHealthKit] Error fetching steps:', err);
      return 0;
    }
  }, [isAuthorized]);

  // Get sleep data for the past N days
  const getSleepData = useCallback(async (days: number = 7): Promise<HealthKitSleepSample[]> => {
    if (!isAuthorized || !healthPluginRef.current) return [];

    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

      const result = await Health.readSamples({
        startDate: startDate.toISOString(),
        endDate: now.toISOString(),
        dataType: 'sleep',
      });

      if ((result as any).samples) {
        return (result as any).samples.map((sample: any) => ({
          startDate: new Date(sample.startDate),
          endDate: new Date(sample.endDate),
          value: sample.sleepState || sample.value || 'asleep',
          sourceName: sample.sourceName,
        }));
      }
      return [];
    } catch (err) {
      console.error('[NativeHealthKit] Error fetching sleep data:', err);
      return [];
    }
  }, [isAuthorized]);

  // Get workouts for the past N days
  const getWorkouts = useCallback(async (days: number = 7): Promise<HealthKitWorkout[]> => {
    if (!isAuthorized || !healthPluginRef.current) return [];

    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

      const result = await Health.queryWorkouts({
        startDate: startDate.toISOString(),
        endDate: now.toISOString(),
      });

      if (result.workouts) {
        return result.workouts.map((workout: any) => ({
          id: workout.id || crypto.randomUUID(),
          activityType: workout.workoutType || 'other',
          startDate: new Date(workout.startDate),
          endDate: new Date(workout.endDate),
          duration: workout.duration || 0,
          energyBurned: workout.totalEnergyBurned,
          distance: workout.totalDistance,
          sourceName: workout.sourceName,
        }));
      }
      return [];
    } catch (err) {
      console.error('[NativeHealthKit] Error fetching workouts:', err);
      return [];
    }
  }, [isAuthorized]);

  // Sync all health data
  const syncHealthData = useCallback(async (forceSync: boolean = false): Promise<void> => {
    if ((!isAuthorizedRef.current && !forceSync) || !healthPluginRef.current) {
      console.log('[NativeHealthKit] Not authorized, skipping sync');
      return;
    }
    // Prevent concurrent syncs (rapid polling can stack)
    if (syncInProgressRef.current && !forceSync) return;
    syncInProgressRef.current = true;

    setIsLoading(true);
    console.log('[NativeHealthKit] Starting health data sync...');

    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);
      const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thirtySixHoursAgo = new Date(now.getTime() - 36 * 60 * 60 * 1000);

      // Fetch all data in parallel
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const [
        heartRateUltraRecentResult,
        heartRateRecentResult,
        heartRateFallbackResult,
        stepsResult,
        caloriesResult,
        distanceResult,
        hrvResult,
        sleepResult,
        workoutsResult,
        spo2Result,
        restingHRResult,
        respiratoryRateResult,
        weightResult,
        bodyTempResult,
        // Expanded types
        bodyFatResult,
        heightResult,
        bloodPressureResult,
        bloodGlucoseResult,
        basalCaloriesResult,
        flightsClimbedResult,
        distanceCyclingResult,
        exerciseTimeResult,
        basalBodyTempResult,
        totalCaloriesResult,
        nutritionResult,
      ] = await Promise.all([
        // Heart rate (last 10m)
        Health.readSamples({
          startDate: tenMinutesAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'heartRate',
        }).catch(() => ({ samples: [], data: [] })),

        // Heart rate (last 2h)
        Health.readSamples({
          startDate: twoHoursAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'heartRate',
        }).catch(() => ({ samples: [], data: [] })),

        // Heart rate fallback (24h)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'heartRate',
        }).catch(() => ({ samples: [], data: [] })),

        // Steps (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'steps',
          bucket: 'day',
        }).catch(() => ({ data: [] })),

        // Calories (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'calories',
          bucket: 'day',
        }).catch(() => ({ data: [] })),

        // Distance (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'distance',
          bucket: 'day',
        }).catch(() => ({ data: [] })),

        // HRV (last 24h)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'heartRateVariability',
        }).catch(() => ({ samples: [], data: [] })),

        // Sleep (36h window)
        Health.readSamples({
          startDate: thirtySixHoursAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'sleep',
        }).catch(() => ({ samples: [], data: [] })),

        // Workouts (last 7 days)
        Health.queryWorkouts({
          startDate: oneWeekAgo.toISOString(),
          endDate: now.toISOString(),
        }).catch(() => ({ workouts: [] })),

        // SpO2 (24h)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'oxygenSaturation',
        }).catch(() => ({ samples: [], data: [] })),

        // Resting Heart Rate (1 week)
        Health.readSamples({
          startDate: oneWeekAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'restingHeartRate',
        }).catch(() => ({ samples: [], data: [] })),

        // Respiratory Rate (24h)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'respiratoryRate',
        }).catch(() => ({ samples: [], data: [] })),

        // Weight (1 week)
        Health.readSamples({
          startDate: oneWeekAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'weight',
        }).catch(() => ({ samples: [], data: [] })),

        // Body Temperature (24h)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'bodyTemperature',
        }).catch(() => ({ samples: [], data: [] })),

        // --- Expanded types ---

        // Body Fat % (1 week — smart scales)
        Health.readSamples({
          startDate: oneWeekAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'bodyFat',
        }).catch(() => ({ samples: [], data: [] })),

        // Height (1 month — rarely changes)
        Health.readSamples({
          startDate: oneMonthAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'height',
        }).catch(() => ({ samples: [], data: [] })),

        // Blood Pressure (1 week — BP monitors)
        Health.readSamples({
          startDate: oneWeekAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'bloodPressure',
        }).catch(() => ({ samples: [], data: [] })),

        // Blood Glucose (24h — CGMs, glucometers)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'bloodGlucose',
        }).catch(() => ({ samples: [], data: [] })),

        // Basal Calories (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'basalCalories',
          bucket: 'day',
        }).catch(() => ({ data: [], samples: [] })),

        // Flights Climbed (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'flightsClimbed',
          bucket: 'day',
        }).catch(() => ({ data: [], samples: [] })),

        // Cycling Distance (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'distanceCycling',
          bucket: 'day',
        }).catch(() => ({ data: [], samples: [] })),

        // Exercise Time (today)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'exerciseTime',
          bucket: 'day',
        }).catch(() => ({ data: [], samples: [] })),

        // Basal Body Temperature (24h — Oura, Natural Cycles)
        Health.readSamples({
          startDate: oneDayAgo.toISOString(),
          endDate: now.toISOString(),
          dataType: 'basalBodyTemperature',
        }).catch(() => ({ samples: [], data: [] })),

        // Total Calories (today — basal + active)
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'totalCalories',
          bucket: 'day',
        }).catch(() => ({ data: [], samples: [] })),

        // Nutrition summary via custom plugin
        HealthKitWater.readNutritionSummary({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
        }).catch(() => null),
      ]);

      // === MULTI-SOURCE DEDUPLICATION ===
      // When multiple wearables (Apple Watch, Whoop, Oura, etc.) report the same
      // metric, HealthKit/Health Connect stores all samples. We deduplicate by
      // picking the most recent sample per metric (for discrete types) and
      // merging overlapping time ranges (for sleep/workouts).

      // Helper: parse sample timestamp safely
      const getSampleTime = (sample: any): number => {
        const raw = sample?.endDate ?? sample?.startDate;
        const ts = new Date(raw).getTime();
        return Number.isFinite(ts) ? ts : -Infinity;
      };

      // Helper: pick latest sample from an array, preferring most recent endDate
      const pickLatestSample = (samples: any[]): any | null => {
        if (samples.length === 0) return null;
        return samples.reduce((latest, sample) => (
          getSampleTime(sample) > getSampleTime(latest) ? sample : latest
        ));
      };

      // Helper: deduplicate overlapping time-range samples (sleep, workouts)
      // Keeps the sample with the longest duration when ranges overlap
      const deduplicateTimeRanges = <T extends { startDate: Date; endDate: Date }>(
        samples: T[],
        overlapThresholdMs: number = 30 * 60 * 1000 // 30 min overlap = duplicate
      ): T[] => {
        if (samples.length <= 1) return samples;
        const sorted = [...samples].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        const result: T[] = [sorted[0]];
        for (let i = 1; i < sorted.length; i++) {
          const prev = result[result.length - 1];
          const curr = sorted[i];
          const overlap = prev.endDate.getTime() - curr.startDate.getTime();
          if (overlap > overlapThresholdMs) {
            // Overlapping — keep the longer one
            const prevDur = prev.endDate.getTime() - prev.startDate.getTime();
            const currDur = curr.endDate.getTime() - curr.startDate.getTime();
            if (currDur > prevDur) {
              result[result.length - 1] = curr;
            }
          } else {
            result.push(curr);
          }
        }
        return result;
      };

      // Parse heart rate — prefer shortest window first to avoid stale selection from truncated long windows
      let latestHeartRate: HealthKitReading | null = null;
      const hrUltraRecentData = (heartRateUltraRecentResult as any).samples || (heartRateUltraRecentResult as any).data || [];
      const hrRecentData = (heartRateRecentResult as any).samples || (heartRateRecentResult as any).data || [];
      const hrFallbackData = (heartRateFallbackResult as any).samples || (heartRateFallbackResult as any).data || [];
      const hrData = hrUltraRecentData.length > 0
        ? hrUltraRecentData
        : hrRecentData.length > 0
          ? hrRecentData
          : hrFallbackData;
      const latestHR = pickLatestSample(hrData);
      if (latestHR) {
        latestHeartRate = {
          value: Math.round(latestHR.value),
          startDate: new Date(latestHR.startDate),
          endDate: new Date(latestHR.endDate),
          unit: 'bpm',
          sourceName: normalizeHealthSourceName(latestHR.sourceName, latestHR.sourceId),
        };
      }

      // Parse HRV — pick single most recent reading across all sources
      let latestHRV: HealthKitReading | null = null;
      const hrvData = (hrvResult as any).samples || hrvResult.data || [];
      const latestHRVSample = pickLatestSample(hrvData);
      if (latestHRVSample) {
        // HealthKit reports HRV (SDNN) in seconds (e.g., 0.033 = 33ms)
        // @capgo/capacitor-health may already return ms. Handle both cases:
        // < 1 → definitely seconds, convert to ms
        // 1-300 → likely already ms
        // > 300 → likely seconds * 1000 already (shouldn't happen, but clamp)
        const rawHRV = latestHRVSample.value;
        let hrvMs: number;
        if (rawHRV < 1) {
          hrvMs = Math.round(rawHRV * 1000);
        } else if (rawHRV <= 300) {
          hrvMs = Math.round(rawHRV); // Already ms
        } else {
          hrvMs = Math.round(rawHRV / 1000); // Abnormally large, assume microseconds
        }
        console.log(`[NativeHealthKit] HRV raw=${rawHRV}, converted=${hrvMs}ms`);
        latestHRV = {
          value: hrvMs,
          startDate: new Date(latestHRVSample.startDate),
          endDate: new Date(latestHRVSample.endDate),
          unit: 'ms',
          sourceName: normalizeHealthSourceName(latestHRVSample.sourceName, latestHRVSample.sourceId),
        };
      }

      // Parse sleep samples — deduplicate overlapping ranges from multiple sources
      // IMPORTANT: HealthKit returns both 'inBed' samples (covering full night) and
      // actual sleep stage segments (core, deep, rem, asleep). We must deduplicate
      // only within the same value category to prevent 'inBed' from swallowing
      // real sleep stages (which would then be filtered out by the UI).
      const sleepData = (sleepResult as any).samples || sleepResult.data || [];

      // === DEBUG: Raw HealthKit sleep payload ===
      console.log('[NativeHealthKit][SleepDebug] Raw sleep samples count:', sleepData.length);
      sleepData.forEach((s: any, i: number) => {
        const startD = new Date(s.startDate);
        const endD = new Date(s.endDate);
        const durationMin = (endD.getTime() - startD.getTime()) / 60000;
        console.log(
          `[NativeHealthKit][SleepDebug] #${i} | state=${s.sleepState || s.value} | src=${s.sourceName} ` +
          `| ${startD.toLocaleTimeString()}-${endD.toLocaleTimeString()} (${durationMin.toFixed(1)} min)`
        );
      });

      const rawSleepSamples: HealthKitSleepSample[] = sleepData.map((s: any) => ({
        startDate: new Date(s.startDate),
        endDate: new Date(s.endDate),
        value: s.sleepState || s.value || 'asleep',
        sourceName: normalizeHealthSourceName(s.sourceName, s.sourceId),
      }));

      // ── Cross-source sleep deduplication ──
      // Multiple wearables (e.g. Whoop + Apple Watch) report overlapping sleep
      // ranges. Grouping only by stage value causes double-counting because
      // each device's "core" segments are in the same group but don't overlap
      // enough to be merged, while one device's "asleep" overlaps another's
      // "core"+"deep"+"rem" in a different group entirely.
      //
      // Strategy: flatten ALL sleep samples (excluding inBed/awake) onto a
      // timeline, merge overlapping intervals, then keep the stage detail from
      // the source with the most granular data for each merged window.

      const actualSleepRaw = rawSleepSamples.filter(
        s => s.value !== 'inBed' && s.value !== 'awake'
      );
      const inBedAndAwake = rawSleepSamples.filter(
        s => s.value === 'inBed' || s.value === 'awake'
      );

      // 1. Group actual-sleep samples by source
      const samplesBySource = new Map<string, HealthKitSleepSample[]>();
      actualSleepRaw.forEach(s => {
        const src = s.sourceName || 'unknown';
        const list = samplesBySource.get(src) || [];
        list.push(s);
        samplesBySource.set(src, list);
      });

      // 2. For each source, calculate total sleep duration & number of stages
      let bestSource = '';
      let bestScore = -1;
      samplesBySource.forEach((samples, src) => {
        const totalMs = samples.reduce(
          (sum, s) => sum + (s.endDate.getTime() - s.startDate.getTime()), 0
        );
        const uniqueStages = new Set(samples.map(s => s.value)).size;
        // Prefer the source with more granular stage data; break ties by duration
        const score = uniqueStages * 1e12 + totalMs;
        if (score > bestScore) {
          bestScore = score;
          bestSource = src;
        }
      });

      console.log(`[NativeHealthKit][SleepDebug] Sources found: ${[...samplesBySource.keys()].join(', ')}. Best source: "${bestSource}"`);

      // 3. Use only the best source's sleep samples for the actual sleep total
      const bestSourceSamples = samplesBySource.get(bestSource) || [];
      const dedupedBestSource = deduplicateTimeRanges(bestSourceSamples);

      // 4. Deduplicate inBed/awake samples independently (these are metadata)
      const dedupedInBedAwake = deduplicateTimeRanges(inBedAndAwake);

      const sleepSamples: HealthKitSleepSample[] = [...dedupedBestSource, ...dedupedInBedAwake];

      // Debug logging
      dedupedBestSource.forEach(s => {
        const durMin = (s.endDate.getTime() - s.startDate.getTime()) / 60000;
        console.log(`[NativeHealthKit][SleepDebug] Kept: ${s.value} from ${s.sourceName} (${durMin.toFixed(1)} min)`);
      });

      const grandTotalHrs = dedupedBestSource
        .reduce((sum, s) => sum + (s.endDate.getTime() - s.startDate.getTime()) / 3600000, 0);
      console.log(`[NativeHealthKit][SleepDebug] Final sleep total (excl inBed/awake): ${grandTotalHrs.toFixed(2)} hrs from "${bestSource}" (${sleepSamples.length} segments)`);


      // Parse workouts — deduplicate overlapping ranges from multiple sources
      const rawWorkouts: HealthKitWorkout[] = ((workoutsResult as any).workouts || []).map((w: any) => ({
        id: w.id || crypto.randomUUID(),
        activityType: w.workoutType || 'other',
        startDate: new Date(w.startDate),
        endDate: new Date(w.endDate),
        duration: w.duration || 0,
        energyBurned: w.totalEnergyBurned,
        distance: w.totalDistance,
        sourceName: normalizeHealthSourceName(w.sourceName, w.sourceId),
      }));
      const workouts = deduplicateTimeRanges(rawWorkouts);

      // Parse SpO2 — pick latest sample
      let latestSpO2: HealthKitReading | null = null;
      const spo2Data = (spo2Result as any).samples || spo2Result.data || [];
      const latestSpO2Sample = pickLatestSample(spo2Data);
      if (latestSpO2Sample) {
        // HealthKit reports SpO2 as a fraction (0.0–1.0), convert to percentage
        const rawValue = latestSpO2Sample.value;
        latestSpO2 = {
          value: rawValue <= 1 ? Math.round(rawValue * 100) : Math.round(rawValue),
          startDate: new Date(latestSpO2Sample.startDate),
          endDate: new Date(latestSpO2Sample.endDate),
          unit: '%',
          sourceName: normalizeHealthSourceName(latestSpO2Sample.sourceName, latestSpO2Sample.sourceId),
        };
      }

      // Parse Resting Heart Rate — pick latest sample
      let latestRestingHR: HealthKitReading | null = null;
      const restingHRData = (restingHRResult as any).samples || restingHRResult.data || [];
      const latestRestingHRSample = pickLatestSample(restingHRData);
      if (latestRestingHRSample) {
        latestRestingHR = {
          value: Math.round(latestRestingHRSample.value),
          startDate: new Date(latestRestingHRSample.startDate),
          endDate: new Date(latestRestingHRSample.endDate),
          unit: 'bpm',
          sourceName: normalizeHealthSourceName(latestRestingHRSample.sourceName, latestRestingHRSample.sourceId),
        };
      }

      // Parse Respiratory Rate — pick latest sample
      let latestRespRate: HealthKitReading | null = null;
      const respRateData = (respiratoryRateResult as any).samples || (respiratoryRateResult as any).data || [];
      const latestRespRateSample = pickLatestSample(respRateData);
      if (latestRespRateSample) {
        latestRespRate = {
          value: Math.round(latestRespRateSample.value * 10) / 10,
          startDate: new Date(latestRespRateSample.startDate),
          endDate: new Date(latestRespRateSample.endDate),
          unit: 'brpm',
          sourceName: normalizeHealthSourceName(latestRespRateSample.sourceName, latestRespRateSample.sourceId),
        };
      }

      // Parse Weight — pick latest sample (from smart scales: Withings, Renpho, eufy, etc.)
      let latestWeight: HealthKitReading | null = null;
      const weightData = (weightResult as any).samples || (weightResult as any).data || [];
      const latestWeightSample = pickLatestSample(weightData);
      if (latestWeightSample) {
        latestWeight = {
          value: Math.round(latestWeightSample.value * 10) / 10,
          startDate: new Date(latestWeightSample.startDate),
          endDate: new Date(latestWeightSample.endDate),
          unit: 'kg',
          sourceName: normalizeHealthSourceName(latestWeightSample.sourceName, latestWeightSample.sourceId),
        };
      }

      // Parse Body Temperature — pick latest sample
      let latestBodyTemp: HealthKitReading | null = null;
      const bodyTempData = (bodyTempResult as any).samples || (bodyTempResult as any).data || [];
      const latestBodyTempSample = pickLatestSample(bodyTempData);
      if (latestBodyTempSample) {
        latestBodyTemp = {
          value: Math.round(latestBodyTempSample.value * 10) / 10,
          startDate: new Date(latestBodyTempSample.startDate),
          endDate: new Date(latestBodyTempSample.endDate),
          unit: '°C',
          sourceName: normalizeHealthSourceName(latestBodyTempSample.sourceName, latestBodyTempSample.sourceId),
        };
      }

      // --- Parse expanded types ---

      // Body Fat %
      let latestBodyFat: HealthKitReading | null = null;
      const bodyFatData = (bodyFatResult as any).samples || (bodyFatResult as any).data || [];
      const latestBodyFatSample = pickLatestSample(bodyFatData);
      if (latestBodyFatSample) {
        const rawVal = latestBodyFatSample.value;
        latestBodyFat = {
          value: rawVal <= 1 ? Math.round(rawVal * 1000) / 10 : Math.round(rawVal * 10) / 10,
          startDate: new Date(latestBodyFatSample.startDate),
          endDate: new Date(latestBodyFatSample.endDate),
          unit: '%',
          sourceName: normalizeHealthSourceName(latestBodyFatSample.sourceName, latestBodyFatSample.sourceId),
        };
      }

      // Height
      let latestHeight: HealthKitReading | null = null;
      const heightData = (heightResult as any).samples || (heightResult as any).data || [];
      const latestHeightSample = pickLatestSample(heightData);
      if (latestHeightSample) {
        latestHeight = {
          value: Math.round(latestHeightSample.value * 100) / 100, // meters → keep precision
          startDate: new Date(latestHeightSample.startDate),
          endDate: new Date(latestHeightSample.endDate),
          unit: 'm',
          sourceName: normalizeHealthSourceName(latestHeightSample.sourceName, latestHeightSample.sourceId),
        };
      }

      // Blood Pressure — BP samples may have systolic/diastolic fields or be separate samples
      let latestBP: BloodPressureReading | null = null;
      const bpData = (bloodPressureResult as any).samples || (bloodPressureResult as any).data || [];
      const latestBPSample = pickLatestSample(bpData);
      if (latestBPSample) {
        latestBP = {
          systolic: Math.round(latestBPSample.systolic || latestBPSample.value || 0),
          diastolic: Math.round(latestBPSample.diastolic || 0),
          startDate: new Date(latestBPSample.startDate),
          sourceName: normalizeHealthSourceName(latestBPSample.sourceName, latestBPSample.sourceId),
        };
      }

      // Blood Glucose
      let latestBloodGlucose: HealthKitReading | null = null;
      const bgData = (bloodGlucoseResult as any).samples || (bloodGlucoseResult as any).data || [];
      const latestBGSample = pickLatestSample(bgData);
      if (latestBGSample) {
        latestBloodGlucose = {
          value: Math.round(latestBGSample.value * 10) / 10,
          startDate: new Date(latestBGSample.startDate),
          endDate: new Date(latestBGSample.endDate),
          unit: 'mg/dL',
          sourceName: normalizeHealthSourceName(latestBGSample.sourceName, latestBGSample.sourceId),
        };
      }

      // Basal Body Temperature
      let latestBasalBodyTemp: HealthKitReading | null = null;
      const basalBTData = (basalBodyTempResult as any).samples || (basalBodyTempResult as any).data || [];
      const latestBasalBTSample = pickLatestSample(basalBTData);
      if (latestBasalBTSample) {
        latestBasalBodyTemp = {
          value: Math.round(latestBasalBTSample.value * 10) / 10,
          startDate: new Date(latestBasalBTSample.startDate),
          endDate: new Date(latestBasalBTSample.endDate),
          unit: '°C',
          sourceName: normalizeHealthSourceName(latestBasalBTSample.sourceName, latestBasalBTSample.sourceId),
        };
      }

      // Aggregated today values
      const basalCaloriesVal = Math.round((basalCaloriesResult as any).samples?.[0]?.value || 0);
      const flightsClimbedVal = Math.round((flightsClimbedResult as any).samples?.[0]?.value || 0);
      const distanceCyclingVal = Math.round((((distanceCyclingResult as any).samples?.[0]?.value || 0) / 1000) * 10) / 10;
      const exerciseTimeVal = Math.round((exerciseTimeResult as any).samples?.[0]?.value || 0);
      const totalCaloriesVal = Math.round((totalCaloriesResult as any).samples?.[0]?.value || 0);

      // Nutrition from custom plugin
      let nutritionData: NutritionSummary | null = null;
      if (nutritionResult && typeof nutritionResult === 'object') {
        const nr = nutritionResult as any;
        nutritionData = {
          caloriesConsumed: Math.round(nr.caloriesConsumed || 0),
          caffeine: Math.round((nr.caffeine || 0) * 10) / 10,
          sodium: Math.round(nr.sodium || 0),
          protein: Math.round((nr.protein || 0) * 10) / 10,
          carbs: Math.round((nr.carbs || 0) * 10) / 10,
          fat: Math.round((nr.fat || 0) * 10) / 10,
        };
        // Only keep if at least one value is non-zero
        if (Object.values(nutritionData).every(v => v === 0)) nutritionData = null;
      }

      // Collect unique source names from all readings — use actual sample timestamps
      const sourceSet = new Map<string, Date>();
      const collectSource = (name?: string, sampleDate?: Date, sourceId?: string) => {
        const normalizedName = normalizeHealthSourceName(name, sourceId);
        if (normalizedName && normalizedName.trim()) {
          const ts = sampleDate || new Date(0);
          const existing = sourceSet.get(normalizedName);
          if (!existing || existing < ts) sourceSet.set(normalizedName, ts);
        }
      };
      if (latestHeartRate?.sourceName) collectSource(latestHeartRate.sourceName, latestHeartRate.endDate);
      if (latestHRV?.sourceName) collectSource(latestHRV.sourceName, latestHRV.endDate);
      if (latestSpO2?.sourceName) collectSource(latestSpO2.sourceName, latestSpO2.endDate);
      if (latestRestingHR?.sourceName) collectSource(latestRestingHR.sourceName, latestRestingHR.endDate);
      if (latestRespRate?.sourceName) collectSource(latestRespRate.sourceName, latestRespRate.endDate);
      if (latestWeight?.sourceName) collectSource(latestWeight.sourceName, latestWeight.endDate);
      if (latestBodyTemp?.sourceName) collectSource(latestBodyTemp.sourceName, latestBodyTemp.endDate);
      if (latestBodyFat?.sourceName) collectSource(latestBodyFat.sourceName, latestBodyFat.endDate);
      if (latestHeight?.sourceName) collectSource(latestHeight.sourceName, latestHeight.endDate);
      if (latestBP?.sourceName) collectSource(latestBP.sourceName, latestBP.startDate);
      if (latestBloodGlucose?.sourceName) collectSource(latestBloodGlucose.sourceName, latestBloodGlucose.endDate);
      if (latestBasalBodyTemp?.sourceName) collectSource(latestBasalBodyTemp.sourceName, latestBasalBodyTemp.endDate);
      // For sleep, use endDate of the most recent segment per source (not all segments)
      const sleepSourceLatest = new Map<string, Date>();
      sleepSamples.forEach(s => {
        const key = s.sourceName || '';
        const existing = sleepSourceLatest.get(key);
        if (!existing || existing < s.endDate) sleepSourceLatest.set(key, s.endDate);
      });
      sleepSourceLatest.forEach((endDate, sourceName) => collectSource(sourceName, endDate));
      workouts.forEach(w => collectSource(w.sourceName, w.endDate));
      const hrSourceLatest = new Map<string, { date: Date; sourceName: string }>();
      hrData.forEach((s: any) => {
        const name = normalizeHealthSourceName(s.sourceName, s.sourceId) || '';
        const ts = new Date(s.endDate || s.startDate);
        const existing = hrSourceLatest.get(name);
        if (!existing || existing.date < ts) hrSourceLatest.set(name, { date: ts, sourceName: name });
      });
      hrSourceLatest.forEach(({ date, sourceName }) => collectSource(sourceName, date));

      // Only include sources seen in the last 24 hours, and deduplicate by normalized name
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const normalizeForDedup = (name: string) => name
        .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
        .replace(/[''`'\u2019\u2018]s\s+/i, ' ')
        .replace(/^.+\s+(apple\s*watch|whoop|oura|garmin|fitbit|polar)/i, '$1')
        .replace(/\s*(series|ultra|se)\s*\d*/gi, '')
        .replace(/\s*\d+(\.\d+)?\s*(mm)?$/i, '')
        .trim()
        .toLowerCase();
      const dedupedSourceMap = new Map<string, { name: string; lastSeen: Date }>();
      sourceSet.forEach((lastSeen, name) => {
        if (lastSeen < twentyFourHoursAgo) return;
        const key = normalizeForDedup(name);
        const existing = dedupedSourceMap.get(key);
        if (!existing || existing.lastSeen < lastSeen) {
          dedupedSourceMap.set(key, { name, lastSeen });
        }
      });
      const sources = Array.from(dedupedSourceMap.values());


      // Steps — queryAggregated only (HealthKit deduplicates internally)
      const stepsRaw = (stepsResult as any).samples?.[0];
      let stepsValue = 0;
      if (stepsRaw) {
        stepsValue = Math.round(stepsRaw.value || 0);
      }
      console.log('[NativeHealthKit] Steps (aggregated, deduped):', stepsValue, 'raw:', JSON.stringify(stepsRaw));

      setHealthData(prev => ({
        ...prev,
        heartRate: latestHeartRate,
        restingHeartRate: latestRestingHR,
        hrv: latestHRV,
        steps: stepsValue,
        calories: Math.round((caloriesResult as any).samples?.[0]?.value || 0),
        distance: Math.round((((distanceResult as any).samples?.[0]?.value || 0) / 1000) * 10) / 10,
        sleep: sleepSamples,
        workouts,
        oxygenSaturation: latestSpO2,
        respiratoryRate: latestRespRate,
        weight: latestWeight,
        bodyTemperature: latestBodyTemp,
        lastSyncAt: new Date(),
        sources: sources.length > 0 ? sources : prev.sources,
        // Expanded types
        bodyFat: latestBodyFat,
        height: latestHeight,
        bloodPressure: latestBP,
        bloodGlucose: latestBloodGlucose,
        basalBodyTemperature: latestBasalBodyTemp,
        basalCalories: basalCaloriesVal,
        flightsClimbed: flightsClimbedVal,
        distanceCycling: distanceCyclingVal,
        exerciseTime: exerciseTimeVal,
        totalCalories: totalCaloriesVal,
        nutrition: nutritionData,
      }));

      console.log('[NativeHealthKit] Health data sync complete:', {
        heartRate: latestHeartRate?.value,
        steps: stepsValue,
        workouts: workouts.length,
        bodyFat: latestBodyFat?.value,
        bloodGlucose: latestBloodGlucose?.value,
        nutrition: nutritionData ? 'present' : 'none',
      });
    } catch (err) {
      console.error('[NativeHealthKit] Error syncing health data:', err);
      setError('Failed to sync health data');
    } finally {
      setIsLoading(false);
      syncInProgressRef.current = false;
    }
  }, []);

  // Fast heart-rate-only poll (2s) — lightweight single query
  const fastPollHeartRate = useCallback(async () => {
    if (!isAuthorizedRef.current || !healthPluginRef.current) return;
    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const fiveMinAgo = new Date(now.getTime() - 5 * 60 * 1000);
      const result = await Health.readSamples({
        startDate: fiveMinAgo.toISOString(),
        endDate: now.toISOString(),
        dataType: 'heartRate',
      });
      const samples = (result as any).samples || [];
      if (samples.length > 0) {
        const latest = samples.reduce((acc: any, sample: any) => {
          const accTime = new Date(acc.endDate || acc.startDate).getTime();
          const sampleTime = new Date(sample.endDate || sample.startDate).getTime();
          return sampleTime > accTime ? sample : acc;
        });
        const latestTime = new Date(latest.endDate || latest.startDate).getTime();
        const newHR: HealthKitReading = {
          value: Math.round(latest.value),
          startDate: new Date(latest.startDate),
          endDate: new Date(latest.endDate),
          unit: 'bpm',
          sourceName: normalizeHealthSourceName(latest.sourceName, latest.sourceId),
        };
        setHealthData(prev => {
          const prevTime = prev.heartRate?.endDate?.getTime() ?? -Infinity;
          const hasNewerSample = latestTime >= prevTime;
          return hasNewerSample
            ? {
                ...prev,
                heartRate: newHR,
                lastSyncAt: new Date(),
              }
            : prev;
        });
      }
    } catch (err) {
      // Silent — full sync will catch errors
    }
  }, []);

  // Fast activity poll (3s) — lightweight aggregated queries for steps, calories, distance, exerciseTime
  const fastPollActivity = useCallback(async () => {
    if (!isAuthorizedRef.current || !healthPluginRef.current) return;
    try {
      const Health = healthPluginRef.current;
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const [stepsRes, caloriesRes, distanceRes, exerciseRes] = await Promise.all([
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'steps',
          bucket: 'day',
        }).catch(() => ({ samples: [] })),
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'calories',
          bucket: 'day',
        }).catch(() => ({ samples: [] })),
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'distance',
          bucket: 'day',
        }).catch(() => ({ samples: [] })),
        Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'exerciseTime',
          bucket: 'day',
        }).catch(() => ({ samples: [] })),
      ]);

      const newSteps = Math.round((stepsRes as any).samples?.[0]?.value || 0);
      const newCalories = Math.round((caloriesRes as any).samples?.[0]?.value || 0);
      const newDistance = Math.round((((distanceRes as any).samples?.[0]?.value || 0) / 1000) * 10) / 10;
      const newExercise = Math.round((exerciseRes as any).samples?.[0]?.value || 0);

      setHealthData(prev => {
        // Only update if values actually changed
        if (prev.steps === newSteps && prev.calories === newCalories && prev.distance === newDistance && prev.exerciseTime === newExercise) return prev;
        return {
          ...prev,
          steps: newSteps,
          calories: newCalories,
          distance: newDistance,
          exerciseTime: newExercise,
          lastSyncAt: new Date(),
        };
      });
    } catch {
      // Silent — full sync will catch errors
    }
  }, []);

  // Check if a workout is currently active and adjust polling
  const checkWorkoutStatus = useCallback(async () => {
    if (!isNative) return;
    try {
      const mod = await import('@/plugins/HealthKitObserverPlugin');
      const result = await mod.default.isWorkoutActive();
      setHealthData(prev => {
        if (prev.isWorkoutActive === result.isActive) return prev;
        console.log(`[NativeHealthKit] Workout active: ${result.isActive}`);
        return { ...prev, isWorkoutActive: result.isActive };
      });

      // If workout is active, switch to ultra-fast 2s HR poll
      if (result.isActive && !workoutPollRef.current) {
        console.log('[NativeHealthKit] Workout detected — switching to 2s HR poll');
        if (hrFastPollRef.current) clearInterval(hrFastPollRef.current);
        workoutPollRef.current = setInterval(fastPollHeartRate, 2_000);
      } else if (!result.isActive && workoutPollRef.current) {
        console.log('[NativeHealthKit] Workout ended — reverting to 5s HR poll');
        clearInterval(workoutPollRef.current);
        workoutPollRef.current = null;
        if (!hrFastPollRef.current) {
          hrFastPollRef.current = setInterval(fastPollHeartRate, 5_000);
        }
      }
    } catch {
      // Observer plugin not installed — no-op
    }
  }, [isNative, fastPollHeartRate]);

  // Start background sync: 15s full sync + 2s HR fast-poll + 3s activity fast-poll + observer queries + workout detection
  const startBackgroundSync = useCallback(async (): Promise<void> => {
    // Clear existing intervals
    if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    if (hrFastPollRef.current) clearInterval(hrFastPollRef.current);
    if (activityFastPollRef.current) clearInterval(activityFastPollRef.current);
    if (workoutPollRef.current) clearInterval(workoutPollRef.current);

    // Initial sync — force it since isAuthorized state may not have updated yet
    await syncHealthData(true);

    // Try to start HealthKit observer queries (push-based — replaces some polling)
    let observerActive = false;
    try {
      const mod = await import('@/plugins/HealthKitObserverPlugin');
      observerPluginRef.current = mod.default;
      await mod.default.startObserving();
      console.log('[NativeHealthKit] Observer queries active — push-based delivery');
      observerActive = true;
      setHealthData(prev => ({ ...prev, deliveryMode: 'observer' }));

      // Listen for push events from HealthKit observers
      if (observerListenerRef.current) observerListenerRef.current.remove?.();
      observerListenerRef.current = await mod.default.addListener('healthDataChanged', (event) => {
        console.log(`[NativeHealthKit] Observer push: ${event.metric} at ${event.timestamp}`);
        // Refresh the specific metric immediately
        if (event.metric === 'heartRate') {
          fastPollHeartRate();
        } else if (['steps', 'calories', 'distance', 'exerciseTime'].includes(event.metric)) {
          fastPollActivity();
        } else {
          // Full sync for other metrics
          syncHealthData();
        }
      });
    } catch {
      console.log('[NativeHealthKit] Observer plugin not available — using polling fallback');
    }

    // Full sync every 10s (15s when observers active, as safety net)
    syncIntervalRef.current = setInterval(() => {
      syncHealthData();
    }, observerActive ? 15_000 : 10_000);

    // Fast HR-only poll every 2s for near-real-time Live Vitals
    hrFastPollRef.current = setInterval(() => {
      fastPollHeartRate();
    }, 2_000);

    // Fast activity poll every 3s for near-real-time steps/calories/distance
    activityFastPollRef.current = setInterval(() => {
      fastPollActivity();
    }, 3_000);

    // Check workout status every 30s
    checkWorkoutStatus();
    const workoutCheckInterval = setInterval(checkWorkoutStatus, 30_000);

    // Instant refresh on app resume (foreground)
    try {
      const { App } = await import('@capacitor/app');
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove?.();
      }
      appStateListenerRef.current = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          console.log('[NativeHealthKit] App resumed — triggering immediate sync');
          syncHealthData();
          fastPollHeartRate();
          fastPollActivity();
          checkWorkoutStatus();
        }
      });
    } catch (err) {
      console.warn('[NativeHealthKit] App listener not available (web?):', err);
    }

    console.log(`[NativeHealthKit] Background sync started (${observerActive ? 'observer + 15s' : '10s'} full + 2s HR + 3s activity fast-poll)`);
  }, [syncHealthData, fastPollHeartRate, fastPollActivity, checkWorkoutStatus]);

  // Stop background sync
  const stopBackgroundSync = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
    if (hrFastPollRef.current) {
      clearInterval(hrFastPollRef.current);
      hrFastPollRef.current = null;
    }
    if (activityFastPollRef.current) {
      clearInterval(activityFastPollRef.current);
      activityFastPollRef.current = null;
    }
    if (workoutPollRef.current) {
      clearInterval(workoutPollRef.current);
      workoutPollRef.current = null;
    }
    if (appStateListenerRef.current) {
      appStateListenerRef.current.remove?.();
      appStateListenerRef.current = null;
    }
    if (observerListenerRef.current) {
      observerListenerRef.current.remove?.();
      observerListenerRef.current = null;
    }
    if (observerPluginRef.current) {
      observerPluginRef.current.stopObserving().catch(() => {});
    }
    console.log('[NativeHealthKit] Background sync stopped');
  }, []);

  return {
    isAvailable,
    isAuthorized,
    isLoading,
    error,
    healthData,
    requestAuthorization,
    syncHealthData,
    getLatestHeartRate,
    getTodaySteps,
    getSleepData,
    getWorkouts,
    startBackgroundSync,
    stopBackgroundSync,
  };
}

export default useNativeHealthKit;
